import { useEffect, useRef, useState } from 'react';
import type { EventsSocketMessage, EventsSocketTopic } from './events-socket';
import { useEventsSocket } from './events-socket';

/*
 * The most often a feed refetches. Socket batches cover every event of every client, up to once a
 * second at peak, so refetching on each could send more requests than the 30s polling these feeds
 * replaced. The first change after a quiet spell refetches at once; later ones inside the window
 * collapse into one refetch at its end. At worst that matches the old polling; idle, it is zero.
 */
const REFRESH_THROTTLE_MS = 30000;

interface Remembered {
  /* Identity of the inputs the rows were fetched for; a different key means the rows do not apply. */
  key: string;
  rows: unknown[];
  /* When the last fetch started, which is what the throttle measures from. */
  fetchedAt: number;
  /* A change arrived that the rows do not reflect yet. */
  needsRefresh: boolean;
}

/*
 * Survives unmounting on purpose. The rail mounts its feeds every time it expands on hover, so state
 * held in the component was thrown away on each mouse-out: every hover refetched all three feeds and
 * showed "no events" while waiting. Rows remembered here render instantly on the next mount.
 *
 * Keyed by scope, so every feed needs its own: two feeds sharing one would overwrite each other's rows.
 */
const memory = new Map<string, Remembered>();

interface Options<T> {
  /* Socket scope and memory slot; one per feed. */
  scope: string;
  /* Stable identity of the query inputs. Keep anything time-derived out of it, or every mount misses. */
  cacheKey: string;
  /* False when there is nothing to ask for, such as favourites with no favourites. */
  enabled: boolean;
  fetch: () => Promise<T[]>;
  /* For a feed that can tell from the ids alone whether a batch concerns it. */
  affects?: (ids: number[]) => boolean;
  /*
   * For a feed that can update itself from what the batch carries. Return the new rows to use them and
   * skip the request, or `undefined` to fall through to a refresh — which is what keeps a feed working
   * when a frame arrives without its `events[]`. Never called before the first rows exist.
   */
  patch?: (rows: T[], message: EventsSocketMessage) => T[] | undefined;
  /* Which socket changes refresh the feed. Defaults to scheduled events. */
  topic?: EventsSocketTopic;
  /* Minimum spacing between refetches. Defaults to `REFRESH_THROTTLE_MS`. */
  throttleMs?: number;
}

/*
 * An events feed that fetches only when it has a reason to: nothing shown yet, or the events socket
 * reported a change. Mounting again with nothing new makes no request at all.
 *
 * `loading` is true only until the first rows exist. After that a refresh keeps showing the previous
 * rows, so the list never blanks out while it updates.
 *
 * Meant for `no-cache` queries: each refresh replaces the rows outright, so an event that left the
 * result also leaves the feed. The shared `getEvents` cache only ever adds.
 */
export const useSocketFeed = <T>({
  scope,
  cacheKey,
  enabled,
  fetch,
  affects,
  patch,
  topic = 'events',
  throttleMs = REFRESH_THROTTLE_MS
}: Options<T>): {
  rows: T[];
  loading: boolean;
} => {
  const socket = useEventsSocket();

  const [rows, setRows] = useState<T[] | undefined>(() => {
    const remembered = memory.get(scope);

    return remembered?.key === cacheKey ? <T[]>remembered.rows : undefined;
  });

  const latestFetch = useRef(fetch);

  latestFetch.current = fetch;

  const latestAffects = useRef(affects);

  latestAffects.current = affects;

  const latestPatch = useRef(patch);

  latestPatch.current = patch;

  useEffect(() => {
    if (!enabled) {
      setRows([]);

      return undefined;
    }

    let cancelled = false;
    let trailing: ReturnType<typeof setTimeout> | undefined;

    const current = (): Remembered | undefined => {
      const remembered = memory.get(scope);

      return remembered?.key === cacheKey ? remembered : undefined;
    };

    const fetchNow = async (): Promise<void> => {
      const previous = <T[]>(current()?.rows ?? []);

      /* Stamped before awaiting, so a second request inside the window does not start a parallel fetch. */
      memory.set(scope, { key: cacheKey, rows: previous, fetchedAt: Date.now(), needsRefresh: false });

      /*
       * The inputs can change while a request is out, like switching validators. The newer fetch has
       * stamped its own key by then, and the older answer must not overwrite it.
       */
      const stillCurrent = (): boolean => memory.get(scope)?.key === cacheKey;

      try {
        const next = await latestFetch.current();

        if (stillCurrent()) {
          memory.set(scope, { key: cacheKey, rows: next, fetchedAt: Date.now(), needsRefresh: false });
        }

        if (!cancelled) {
          setRows(next);
        }
      } catch {
        /* Keep what is shown and try again on the next reason to. */
        if (stillCurrent()) {
          memory.set(scope, { key: cacheKey, rows: previous, fetchedAt: Date.now(), needsRefresh: true });
        }

        if (!cancelled) {
          setRows(previous);
        }
      }
    };

    const request = (): void => {
      const remembered = current();

      if (!remembered) {
        void fetchNow();

        return;
      }

      if (!remembered.needsRefresh) {
        return;
      }

      const elapsed = Date.now() - remembered.fetchedAt;

      if (elapsed >= throttleMs) {
        void fetchNow();

        return;
      }

      if (trailing !== undefined) {
        return;
      }

      trailing = setTimeout(() => {
        trailing = undefined;
        void fetchNow();
      }, throttleMs - elapsed);
    };

    const markNeedsRefresh = (): void => {
      const remembered = current();

      if (remembered) {
        memory.set(scope, { ...remembered, needsRefresh: true });
      }
    };

    /* Changes that arrived while the feed was not mounted. */
    if (socket.consumeDirty(scope)) {
      markNeedsRefresh();
    }

    request();

    const applyPatch = (message: EventsSocketMessage): boolean => {
      const remembered = current();

      /* Nothing fetched yet: there is no list to patch, and the first fetch will carry the change. */
      if (!remembered || !latestPatch.current) {
        return false;
      }

      const next = latestPatch.current(<T[]>remembered.rows, message);

      if (!next) {
        return false;
      }

      memory.set(scope, { ...remembered, rows: next });

      if (!cancelled) {
        setRows(next);
      }

      return true;
    };

    const unsubscribe = socket.subscribe(scope, (message) => {
      /* A resync concerns every feed: anything may have been missed while the socket was down. */
      if (message.kind !== 'resync' && message.kind !== topic) {
        return;
      }

      /* A feed that can read the batch updates itself; only what it cannot read costs a request. */
      if (message.kind === 'events' && applyPatch(message)) {
        return;
      }

      /*
       * Deletions travel apart from the ids, so a feed that did not patch them away keeps what it has
       * rather than refetching — the same as before deletions were announced at all.
       */
      if (message.kind === 'events' && !message.ids.length) {
        return;
      }

      if (message.kind === 'events' && latestAffects.current && !latestAffects.current(message.ids)) {
        return;
      }

      markNeedsRefresh();
      request();
    }, topic);

    return (): void => {
      cancelled = true;
      unsubscribe();

      /* A pending refresh stays flagged in memory, so the next mount picks it up. */
      if (trailing !== undefined) {
        clearTimeout(trailing);
      }
    };
  }, [socket, scope, cacheKey, enabled, topic, throttleMs]);

  return { rows: rows ?? [], loading: enabled && rows === undefined };
};
